// Chemin: C:\gestion-copro\src\types\mandat.types.ts
// Types pour le mandat de coordination - Gouvernance V2
// Session 88 : création initiale
// Référence : SPECIFICATION_GOUVERNANCE_V2.md §5

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// STATUTS DU MANDAT
// ============================================================================

/**
 * Statut du mandat de coordination
 * - en_attente_signatures : créé, en attente que les membres CS signent
 * - actif : tous les membres CS ont signé, le coordinateur est mandaté
 * - revoque : mandat annulé par vote majoritaire du CS
 * - expire : mandat arrivé à échéance (fin de mandat CS)
 */
export type StatutMandat = 'en_attente_signatures' | 'actif' | 'revoque' | 'expire';

// ============================================================================
// SIGNATURE D'UN MEMBRE CS
// ============================================================================

/**
 * Enregistrement de la signature d'un membre CS
 * Chaque membre signe individuellement à sa première connexion
 */
export interface SignatureMandat {
  membreCSId: string;           // ID du document dans la collection membresCS
  userId: string;               // ID Firebase Auth du membre
  nom: string;                  // Nom complet affiché
  dateSignature: Timestamp;     // Horodatage précis
  ip: string;                   // Adresse IP au moment de la signature
  confirmationTexte: string;    // Texte exact validé : "Je confirme la désignation de [NOM]..."
}

// ============================================================================
// MANDAT DE COORDINATION
// ============================================================================

/**
 * Mandat de coordination
 * 
 * Acte par lequel les membres du CS délèguent au coordinateur
 * le pouvoir d'agir en leur nom dans la gestion courante via Copro Protect.
 * Base juridique : articles 1984 à 2010 du Code civil.
 * 
 * Collection Firestore : coproprietes/{coproprieteId}/mandats/{mandatId}
 * → Sous-collection de la copropriété pour isolation multi-tenant
 */
export interface MandatCoordination {
  id: string;
  coproprieteId: string;
  
  // --- Coordinateur désigné ---
  coordinateurId: string;       // userId Firebase Auth
  coordinateurNom: string;      // "Prénom NOM" pour affichage
  estMembreCS: boolean;         // true = scénario A (élu), false = scénarios B ou C
  
  // --- Dates et statut ---
  dateCreation: Timestamp;
  statut: StatutMandat;
  dateActivation?: Timestamp;   // Quand tous ont signé
  dateRevocation?: Timestamp;   // Si révoqué
  dateExpiration?: Timestamp;   // Fin prévue du mandat CS
  
  // --- Signatures des membres CS ---
  /** 
   * Map des signatures : clé = membreCSId (ID du document membresCS)
   * Rempli progressivement au fur et à mesure des connexions
   */
  signatures: {
    [membreCSId: string]: SignatureMandat;
  };
  
  /**
   * Nombre total de signatures attendues
   * Fixé à la création du mandat (= nombre de membres CS actifs)
   */
  signaturesAttendues: number;
  
  // --- Document PDF ---
  pdfUrl?: string;              // URL Firebase Storage du PDF généré
  pdfGenereAt?: Timestamp;      // Date de génération du PDF
  
  // --- Métadonnées ---
  createdBy: string;            // userId de l'Admin SaaS qui a créé le mandat
  
  // --- Contexte juridique (stocké pour le PDF) ---
  nomCopropriete: string;       // Nom de la résidence
  dateAG?: Timestamp;           // Date de l'AG ayant élu le CS
  refAG?: string;               // Référence du PV d'AG
}

// ============================================================================
// DONNÉES POUR CRÉATION
// ============================================================================

/**
 * Données nécessaires pour créer un nouveau mandat
 * L'Admin SaaS fournit ces informations lors de l'onboarding
 */
export interface MandatCreateData {
  coproprieteId: string;
  coordinateurId: string;
  coordinateurNom: string;
  estMembreCS: boolean;
  nomCopropriete: string;
  dateAG?: Date;
  refAG?: string;
  /** Liste des membreCSId attendus pour signature */
  membresCSIds: string[];
}

// ============================================================================
// DONNÉES POUR LE SUIVI (DASHBOARD COORDINATEUR)
// ============================================================================

/**
 * Résumé du mandat pour affichage dans le dashboard
 */
export interface MandatSuivi {
  id: string;
  statut: StatutMandat;
  coordinateurNom: string;
  estMembreCS: boolean;
  signaturesAttendues: number;
  signaturesRecues: number;
  /** Détail par membre : nom + signé ou non */
  detailSignatures: MandatSignatureDetail[];
  dateCreation: Timestamp;
  dateActivation?: Timestamp;
}

/**
 * Détail d'une signature individuelle (pour le tableau de suivi)
 */
export interface MandatSignatureDetail {
  membreCSId: string;
  nom: string;
  signe: boolean;
  dateSignature?: Timestamp;
}

// ============================================================================
// DONNÉES POUR LA PAGE DE SIGNATURE (MEMBRE CS)
// ============================================================================

/**
 * Informations affichées au membre CS lors de la signature
 */
export interface MandatSignaturePageData {
  mandatId: string;
  coproprieteId: string;
  nomCopropriete: string;
  coordinateurNom: string;
  estMembreCS: boolean;
  dateAG?: Timestamp;
  /** true si ce membre a déjà signé */
  dejaSigne: boolean;
  /** Texte du mandat à afficher (généré dynamiquement) */
  texteMandat: string;
}

// ============================================================================
// LABELS ET CONSTANTES
// ============================================================================

export const STATUT_MANDAT_LABELS: Record<StatutMandat, string> = {
  en_attente_signatures: 'En attente des signatures',
  actif: 'Actif',
  revoque: 'Révoqué',
  expire: 'Expiré'
};

export const STATUT_MANDAT_COLORS: Record<StatutMandat, string> = {
  en_attente_signatures: 'bg-amber-100 text-amber-800',
  actif: 'bg-green-100 text-green-800',
  revoque: 'bg-red-100 text-red-800',
  expire: 'bg-gray-100 text-gray-600'
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Vérifie si un mandat est actif (signé par tous)
 */
export function isMandatActif(mandat: MandatCoordination): boolean {
  return mandat.statut === 'actif';
}

/**
 * Vérifie si un membre CS a signé le mandat
 */
export function aSigneMandat(mandat: MandatCoordination, membreCSId: string): boolean {
  return !!mandat.signatures[membreCSId];
}

/**
 * Calcule le nombre de signatures reçues
 */
export function getNbSignatures(mandat: MandatCoordination): number {
  return Object.keys(mandat.signatures).length;
}

/**
 * Vérifie si toutes les signatures sont collectées
 */
export function toutesSignaturesCollectees(mandat: MandatCoordination): boolean {
  return getNbSignatures(mandat) >= mandat.signaturesAttendues;
}

/**
 * Calcule le pourcentage de progression des signatures
 */
export function getPourcentageSignatures(mandat: MandatCoordination): number {
  if (mandat.signaturesAttendues === 0) return 100;
  return Math.round((getNbSignatures(mandat) / mandat.signaturesAttendues) * 100);
}

/**
 * Génère le texte de confirmation pour un membre CS
 * Ex: "Je confirme la désignation de Dominique SERGENT comme coordinateur..."
 */
export function genererTexteConfirmation(coordinateurNom: string, nomCopropriete: string): string {
  return `Je confirme la désignation de ${coordinateurNom} en qualité de Coordinateur/Coordinatrice pour agir au nom du Conseil Syndical de la copropriété ${nomCopropriete} dans le cadre de la gestion courante via la plateforme Copro Protect.`;
}

/**
 * Génère le texte complet du mandat (pour affichage et PDF)
 */
export function genererTexteMandat(
  nomCopropriete: string,
  coordinateurNom: string,
  dateAG?: string
): string {
  const dateAGTexte = dateAG ? ` du ${dateAG}` : '';
  
  return `MANDAT DE COORDINATION

Copropriété : ${nomCopropriete}

Les membres soussignés du Conseil Syndical de la copropriété ${nomCopropriete}, désignés par l'Assemblée Générale${dateAGTexte}, mandatent :

${coordinateurNom}

en qualité de Coordinateur/Coordinatrice pour agir au nom du Conseil Syndical dans le cadre de la gestion courante via la plateforme Copro Protect.

Périmètre du mandat :

Ce mandat couvre notamment :
- La rédaction et l'envoi de correspondances courantes (demandes d'information, relances, réclamations)
- L'archivage et la classification des documents dans la GED
- Le suivi des signalements et la gestion des affaires
- La gestion des interlocuteurs (syndic, promoteur, prestataires)
- L'utilisation de l'assistant SENTINELLE pour l'analyse et la préparation des communications
- La communication avec le syndic, les prestataires et les copropriétaires

Exclusions du mandat :

Sont exclus de ce mandat et soumis à validation préalable d'au moins un membre du Conseil Syndical :
- Les mises en demeure formelles
- Les courriers précontentieux et contentieux
- Toute communication impliquant une menace de procédure judiciaire
- Tout engagement financier au nom du Conseil Syndical

Droit de regard :

Chaque membre du Conseil Syndical conserve un droit de consultation permanent sur l'ensemble des actions réalisées via la plateforme Copro Protect. L'historique complet des communications, documents et décisions est accessible à tout moment.

Droit d'alerte :

Tout membre du Conseil Syndical peut signaler a posteriori une communication qu'il estime inappropriée via la fonction "Signaler un problème" de la plateforme. Ce signalement est tracé et notifié à l'ensemble du Conseil.

Durée :

Ce mandat est valable pour la durée du mandat du Conseil Syndical en cours, sauf révocation anticipée par vote majoritaire des membres du CS.`;
}
